"use client";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Loader2, Mail } from "lucide-react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { login } from "@/modules/auth/api";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const loginSchema = z.object({
  email: z.string().email({ message: "Ingresa un correo válido" }),
  password: z.string().min(6, { message: "La contraseña debe tener al menos 6 caracteres" }),
  remember: z.boolean().optional(),
});

type LoginSchema = z.infer<typeof loginSchema>;

export function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const router = useRouter();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      remember: true,
    },
  });

  // Resetear estado de loading cuando el componente se monte
  useEffect(() => {
    setIsLoggingIn(false);
  }, []);

  const onSubmit = async (values: LoginSchema) => {
    setIsLoggingIn(true);
    try {
      await login({
        email: values.email,
        password: values.password,
        remember: values.remember,
      });
      
      console.log('✅ [LOGIN FORM] Login exitoso, redirigiendo al dashboard...');
      
      // Resetear estado antes de redireccionar
      setIsLoggingIn(false);
      
      // SOLUCIÓN MÓVIL: Forzar redirección con window.location
      // Esto asegura que funcione en todos los dispositivos
      // Detectar si estamos bajo /app/ desde la URL actual
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/app/')) {
          window.location.href = '/app/dashboard';
        } else {
          window.location.href = '/dashboard';
        }
      } else {
        router.replace("/dashboard");
      }
      
    } catch (error) {
      console.error(error);
      toast.error("No pudimos iniciar sesión. Verifica tus credenciales o consulta al administrador.");
      setIsLoggingIn(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground" htmlFor="email">
          Correo electrónico
        </label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="email"
            type="email"
            placeholder="usuario@empresa.com"
            className={`pl-10 h-9 ${errors.email ? 'border-red-500' : ''}`}
            {...register("email")}
          />
        </div>
        {errors.email ? (
          <p className="text-xs text-red-500">{errors.email.message}</p>
        ) : (
          <p className="text-xs text-muted-foreground">Usa el correo corporativo asignado por tu empresa.</p>
        )}
      </div>

      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground" htmlFor="password">
          Contraseña
        </label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder="Ingresa tu contraseña"
            className={`pr-10 h-9 ${errors.password ? 'border-red-500' : ''}`}
            {...register("password")}
          />
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.password ? (
          <p className="text-xs text-red-500">{errors.password.message}</p>
        ) : (
          <p className="text-xs text-muted-foreground">Solicita al administrador si olvidaste tu contraseña.</p>
        )}
      </div>

      <div className="flex items-center justify-between text-xs">
        <label className="inline-flex items-center gap-2 text-muted-foreground">
          <input type="checkbox" className="h-3 w-3 rounded border-border" {...register("remember")} />
          Recordarme
        </label>
        <button type="button" className="text-primary hover:underline">
          ¿Olvidaste tu contraseña?
        </button>
      </div>

      <Button type="submit" className="w-full h-9" disabled={isLoggingIn}>
        {isLoggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ingresar"}
      </Button>

      {isLoggingIn && (
        <p className="text-center text-xs text-muted-foreground animate-pulse">
          Verificando Acceso...
        </p>
      )}

      <p className="text-center text-xs text-muted-foreground">
        ¿Necesitas acceso? Contacta al administrador de tu empresa.
      </p>
    </form>
  );
}
